import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faUserEdit, faTrash, faCommentDollar, faPenToSquare, faDollar } from '@fortawesome/free-solid-svg-icons'
import { Formik, Form, Field } from 'formik'
import CustomLoader from '../layouts/CustomLoader'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface MarkState {
  name: string
}

interface MarkPrice {
  country    : string
  States     : MarkState[]
  description: string
  months     : number
}

interface Freemium {
  email   : string
  phone   : string
  whatsapp: string
  banners : string[]
  link    : string[]
}

interface MarkPayment {
  pi_stripe_id       : string | null
  count_subscription : number
  status_suscription : string
  total_amount       : number
  Markprice          : MarkPrice
  Freemium           : Freemium | null
}

interface MarketingUser {
  id   : number
  email: string
}

interface Marketing {
  id                    : number
  name                  : string
  description           : string
  street_address        : string
  address_line_2        : string
  zip_code              : string
  city                  : string
  state                 : string
  country               : string
  invoice_street_address: string
  invoice_address_line_2: string
  invoice_zip_code      : string
  invoice_city          : string
  invoice_state         : string
  invoice_country       : string
  invoice_email         : string
  price                 : number
  Users                 : MarketingUser[]
  Markpayments          : MarkPayment[]
}

const pill = (bg: string): React.CSSProperties => ({
  display        : 'inline-flex',
  alignItems     : 'center',
  gap            : '0.3rem',
  padding        : '0.28rem 0.65rem',
  borderRadius   : 9999,
  backgroundColor: bg,
  color          : '#fff',
  fontWeight     : 700,
  fontSize       : '0.75rem',
  border         : 'none',
  cursor         : 'pointer',
  boxShadow      : `0 2px 6px ${bg}44`,
  textDecoration : 'none',
  whiteSpace     : 'nowrap' as const,
})

const PaymentRow: React.FC<{ payment: MarkPayment; urlBase: string; t: (k: string) => string; idx: number }> = ({ payment: e, urlBase, t, idx }) => {
  const [showBanners, setShowBanners] = useState(false)
  return (
    <div style={{
      display            : 'grid',
      gridTemplateColumns: '1fr 1fr',
      borderBottom       : '1px solid #f1f5f9',
      fontSize           : '0.82rem',
    }}>
      {/* Payment info */}
      <div style={{ padding: '0.75rem 1rem', borderRight: '1px solid #f1f5f9' }}>
        <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>
          {t('marketing.payment')}
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <li><b>{t('marketing.count')}:</b> {e.count_subscription ?? '—'}</li>
          <li><b>{t('marketing.pi_id')}:</b> {e.pi_stripe_id ?? '—'}</li>
          <li><b>{t('marketing.status')}:</b> {e.status_suscription ?? '—'}</li>
          <li><b>{t('marketing.total')}:</b> {e.total_amount ?? '—'}</li>
          <li><b>{t('marketing.country')}:</b> {e.Markprice?.country ?? '—'}</li>
          <li>
            <b>{t('marketing.states')}:</b>{' '}
            {e.Markprice?.States?.map(s => s.name).join(', ') || '—'}
          </li>
          <li><b>{t('marketing.description')}:</b> {e.Markprice?.description ?? '—'}</li>
          <li><b>{t('marketing.months')}:</b> {e.Markprice?.months ?? '—'}</li>
        </ul>
      </div>

      {/* Banners info */}
      <div style={{ padding: '0.75rem 1rem' }}>
        <p style={{ margin: '0 0 0.5rem', fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase' }}>
          {t('marketing.banners')}
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <li><b>{t('marketing.email')}:</b> {e.Freemium?.email ?? '—'}</li>
          <li><b>{t('marketing.phone')}:</b> {e.Freemium?.phone ?? '—'}</li>
          <li><b>WhatsApp:</b> {e.Freemium?.whatsapp ?? '—'}</li>
          <li style={{ marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setShowBanners(p => !p)}
              style={{
                ...pill('#3b82f6'),
                marginBottom: showBanners ? '0.5rem' : 0,
              }}
            >
              {showBanners ? '▲' : '▼'} {t('marketing.show_banners')}
            </button>
            {showBanners && (
              <div style={{ marginTop: '0.5rem' }}>
                {(e.Freemium?.banners?.length ?? 0) > 0
                  ? e.Freemium!.banners.map((f, j) => (
                      <div key={j} style={{
                        display      : 'flex',
                        gap          : '0.75rem',
                        alignItems   : 'center',
                        padding      : '0.5rem',
                        border       : '1px solid #e2e8f0',
                        borderRadius : 8,
                        marginBottom : '0.5rem',
                      }}>
                        <img
                          src={`${urlBase}freemium/${f}`}
                          width={100}
                          style={{ borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                          alt={e.Freemium?.link?.[j] ?? ''}
                        />
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#475569', wordBreak: 'break-all' }}>
                          {e.Freemium?.link?.[j] ?? '—'}
                        </p>
                      </div>
                    ))
                  : <p style={{ color: '#94a3b8', fontSize: '0.82rem' }}>No banners :(</p>
                }
              </div>
            )}
          </li>
        </ul>
      </div>
    </div>
  )
}

const ExpandedComponent: React.FC<{ data: Marketing; urlBase: string; t: (k: string) => string }> = ({ data: mk, urlBase, t }) => {
  if (!mk.Markpayments.length) {
    return <div style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.85rem' }}>{t('marketing.no_payments')}</div>
  }
  return (
    <>
      {mk.Markpayments.map((payment, i) => (
        <PaymentRow key={`${payment.pi_stripe_id}-${i}`} payment={payment} urlBase={urlBase} t={t} idx={i} />
      ))}
    </>
  )
}

const Marketings: React.FC = () => {
  const { getHeaders }             = useContext(AuthContext) as AuthCtx
  const { t }                      = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const [data, setData]                             = useState<Marketing[]>([])
  const [loading, setLoading]                       = useState(false)
  const [totalRows, setTotalRows]                   = useState(0)
  const [perPage, setPerPage]                       = useState(10)
  const [search, setSearch]                         = useState('')
  const [searchOpen, setSearchOpen]                 = useState(false)
  const [resetPaginationToggle, setResetPagination] = useState(false)

  const fetchMarketings = async (
    page      : number,
    perPageVal = perPage,
    searchVal  = search
  ) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: Marketing[]; total: number }>(
        `${url}marketings?page=${page}&per_page=${perPageVal}&search=${searchVal}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchMarketings(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchMarketings(page, newPerPage)
  }
  const handleSearch = (values: { search: string }) => {
    setSearch(values.search)
    fetchMarketings(1, perPage, values.search)
  }

  const deleteMarketing = async (id: number) => {
    const result = await Swal.fire({
      title            : t('marketing.delete_title'),
      text             : t('marketing.delete_txt'),
      showCancelButton : true,
      confirmButtonText: t('marketing.delete'),
      icon             : 'warning',
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      const res     = await axios.delete<{ response: string }>(
        `${url}marketings/destroy/${id}`, { headers }
      )
      Swal.fire({ icon: 'success', title: res.data.response })
      fetchMarketings(1)
      setResetPagination(p => !p)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  useEffect(() => { fetchMarketings(1) }, [])

  const columns = [
    {
      name    : t('marketing.name'),
      maxWidth: '160px',
      cell    : (row: Marketing) => (
        <div>
          <p style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }} className="text-capitalize">{row.name}</p>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.78rem' }} className="text-capitalize">{row.description}</p>
        </div>
      ),
    },
    {
      name: t('marketing.address'),
      cell: (row: Marketing) => (
        <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0' }}>
          {[row.street_address, row.address_line_2, row.zip_code, row.city, row.state, row.country].filter(Boolean).map((v, i) => (
            <li key={i} className="small text-capitalize">{v}</li>
          ))}
        </ul>
      ),
    },
    {
      name: t('marketing.address_invoice'),
      cell: (row: Marketing) => (
        <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0' }}>
          {[row.invoice_street_address, row.invoice_address_line_2, row.invoice_zip_code, row.invoice_city, row.invoice_state, row.invoice_country, row.invoice_email].filter(Boolean).map((v, i) => (
            <li key={i} className="small">{v}</li>
          ))}
        </ul>
      ),
    },
    {
      name    : t('marketing.price_4_state'),
      maxWidth: '100px',
      cell    : (row: Marketing) => <span className="small fw-bold">${row.price}</span>,
    },
    {
      name: t('marketing.users'),
      cell: (row: Marketing) => (
        <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0', width: '100%' }}>
          {row.Users.length > 0
            ? row.Users.map((u, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
                  <span className="small">{u.email}</span>
                  <Link to={`/users/edit/${u.id}`} style={pill(BRAND)}>
                    <FontAwesomeIcon icon={faUserEdit} />
                  </Link>
                </li>
              ))
            : <li className="small text-muted">No users found</li>
          }
        </ul>
      ),
    },
    {
      name    : t('marketing.actions'),
      minWidth: '200px',
      cell    : (row: Marketing) => (
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', padding: '4px 0' }}>
          <Link to={`edit/${row.id}`} style={pill(BRAND)}>
            <FontAwesomeIcon icon={faPenToSquare} />
            <span>{t('marketing.edit')}</span>
          </Link>
          <Link to="/payments/admin/marketings" style={pill('#3b82f6')}>
            <FontAwesomeIcon icon={faDollar} />
            <span>{t('marketing.payment')}</span>
          </Link>
          <button onClick={() => deleteMarketing(row.id)} style={pill('#ef4444')}>
            <FontAwesomeIcon icon={faTrash} />
            <span>{t('marketing.delete')}</span>
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="row align-items-start">

      {/* Header label */}
      <div className="col-sm-4 col-md-2 mb-3 my-1">
        <span className="text-capitalize fw-bold">{t('marketing.options') || 'Marketing'}</span>
        <p className="text-muted small mt-1">Marketing accounts</p>
      </div>

      {/* Search panel */}
      <div className="col-sm-8 col-md-10 mb-3">
        <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <button
            type="button"
            onClick={() => setSearchOpen(p => !p)}
            style={{
              width      : '100%',
              display    : 'flex',
              alignItems : 'center',
              gap        : '0.5rem',
              padding    : '0.75rem 1.25rem',
              background : searchOpen ? '#f8fafc' : '#fff',
              border     : 'none',
              borderBottom: searchOpen ? '1px solid #e2e8f0' : 'none',
              cursor     : 'pointer',
              fontWeight : 700,
              fontSize   : '0.9rem',
              color      : '#475569',
              textAlign  : 'left',
            }}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span>{t('utilities.search')}</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
          </button>
          {searchOpen && (
            <div style={{ padding: '1.25rem' }}>
              <Formik initialValues={{ search }} onSubmit={handleSearch} enableReinitialize>
                <Form>
                  <div className="row align-items-center mb-3">
                    <label htmlFor="search" className="col-sm-2 col-form-label col-form-label-sm">{t('marketing.search')}</label>
                    <div className="col-sm-10">
                      <Field type="text" className="form-control form-control-sm" name="search" id="search" placeholder={t('marketing.search')} />
                    </div>
                  </div>
                  <button className="btn btn-sm btn-bd-primary" type="submit">
                    <span className="fw-bold">{t('utilities.search_btn')}</span>
                  </button>
                </Form>
              </Formik>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="col-md-12 mb-3">
        <Link
          to="create"
          style={{
            display       : 'inline-flex',
            alignItems    : 'center',
            gap           : '0.45rem',
            padding       : '0.5rem 1.25rem',
            borderRadius  : 9999,
            background    : `linear-gradient(135deg, ${BRAND_DARK}, ${BRAND})`,
            color         : '#fff',
            fontWeight    : 700,
            fontSize      : '0.875rem',
            textDecoration: 'none',
            boxShadow     : `0 4px 14px ${BRAND}55`,
          }}
        >
          <FontAwesomeIcon icon={faCommentDollar} />
          <span>{t('marketing.add_marketing')}</span>
        </Link>
      </div>

      {/* DataTable */}
      <div className="col-md-12">
        <div className="card card-shadow">
          <DataTable
            columns={columns}
            data={data}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            progressPending={loading}
            paginationResetDefaultPage={resetPaginationToggle}
            pagination
            paginationPerPage={perPage}
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationComponentOptions={paginationComponentOptions}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            progressComponent={<CustomLoader />}
            dense
            expandableRows
            expandableRowsComponent={(props: any) => (
              <ExpandedComponent {...props} urlBase={url ?? ''} t={t} />
            )}
          />
        </div>
      </div>

    </div>
  )
}

export default Marketings
